'use client'

import { SendMsgIcon } from '@/components/svg/send-msg-icon/send-msg-icon'
import { XIconNew } from '@/components/svg/x-icon-new/x-icon-new'
import { ButtonV4 } from '@/components/v4/atoms/button/button'
import { useFormik } from 'formik'
import Link from 'next/link'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { toast } from 'sonner'
import { submitEnquiry } from './supplier-enquiry-form.server'

import { CheckboxV3 } from '@/components/v3/atoms/checkbox/checkbox'
import { DatepickerButtonV3 } from '@/components/v3/atoms/datepicker-button/datepicker-button'
import { InputGroupV3 } from '@/components/v3/molecules/input-group/input-group'
import { formatWeddingDate } from '@/components/v3/unique/supplier/supplier-enquiry-form/utils'
import {
  GTM_EVENT,
  RECAPTCHA_ACTIONS,
  ROUTES,
  SUPPLIER_SECTION_ID,
} from '@/constants'
import { ErrorCode } from '@/errors/errorCode'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useModal } from '@/hooks/use-modal'
import { useSignInModal } from '@/hooks/use-sign-in-modal'
import { useUser } from '@/hooks/use-user'
import { sendGTMEvent } from '@next/third-parties/google'
import { format } from 'date-fns'
import { useRouter } from 'next/navigation'
import { useEffect, useRef } from 'react'
import * as yup from 'yup'
import s from './supplier-enquiry-form.module.scss'

const enquirySchema = yup.object({
  name: yup.string().required('This field is required.'),
  email: yup
    .string()
    .email('This field must be a valid email address.')
    .required('This field is required.'),
  date: yup.string().required('This field is required.'),
  guests: yup
    .string()
    .matches(/^\d+$/, 'This field must be a number.')
    .required('This field is required.'),
  message: yup.string().required('This field is required.'),
  newsletter: yup.boolean(),
})

export type EnquiryFormValues = yup.InferType<typeof enquirySchema>

type Props = {
  handleCancelBtn?: () => void
  title?: string
  supplierId: number
  businessCategoryName?: string
  supplierSlug?: string
  supplierTitle?: string
  initialValues?: EnquiryFormValues
}

export const SupplierEnquiryForm = ({
  handleCancelBtn,
  title = 'Enquire Now',
  supplierId,
  businessCategoryName,
  supplierSlug,
  supplierTitle,
  initialValues,
}: Props) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const { user, isReader, isSupplier, isAdmin } = useUser()
  const breakpoint = useBreakpoint()
  const isDesktop = breakpoint?.includes('desktop')
  const router = useRouter()
  const modal = useModal()
  const signInModal = useSignInModal()

  const hasInteracted = useRef(false)

  const loggedUserName = isReader
    ? `${user!.first_name} ${user!.last_name}`
    : ''
  const usersEmail = isReader ? user!.email : ''
  const usersWeddingDate = isReader
    ? formatWeddingDate(user?.event.wedding_date, user?.event.wedding_date_type)
    : ''
  const usersNumberOfGuests = isReader
    ? String(user?.event?.number_of_guests || '')
    : ''

  const formik = useFormik({
    initialValues: {
      name: loggedUserName,
      email: usersEmail,
      date: usersWeddingDate,
      guests: usersNumberOfGuests,
      message: `Hi! We're planning our wedding and would love to hear more about what you offer and your availability.`,
      newsletter: false,
      ...(initialValues || {}),
    },
    validationSchema: enquirySchema,
    validateOnMount: true,
    onSubmit: async (values, { resetForm }) => {
      if (!isReader) {
        signInModal.open({
          modalProps: {
            disabledRedirect: true,
            onSuccess: () => {
              formik.handleSubmit()
            },
          },
        })

        return
      }

      const token = await executeRecaptcha?.(RECAPTCHA_ACTIONS.ENQUIRY_FORM)
      if (!token) {
        console.log(ErrorCode.e00032, 'Recaptcha token is empty')
        toast.error(
          `${ErrorCode.e00032}: ReCAPTCHA validation failed - please try again.`
        )
        return
      }

      const { success, message, relatedSuppliers } = await submitEnquiry({
        ...values,
        recaptchaToken: token,
        supplierId,
        userId: user?.id,
      })

      if (!success) {
        console.log(ErrorCode.e00132, message)
        toast.error(message)
        return
      }

      resetForm()
      sendGTMEvent({
        event: GTM_EVENT.ENQUIRY_SUBMIT,
        'profile url': window.location.href,
        category: businessCategoryName,
      })

      if (!isDesktop) {
        modal.open('supplier-enquiry-form-success', {
          modalProps: {
            relatedSuppliers,
            supplierTitle: supplierTitle || '',
            supplierSlug: supplierSlug || '',
          },
        })
        return
      }

      router.push(`${supplierSlug || ''}?submitted=true`, {
        scroll: false,
      })
    },
  })

  const fieldsDisabled = isSupplier || isAdmin
  const formDisabled = !formik.isValid || formik.isSubmitting || fieldsDisabled

  useEffect(() => {
    if (initialValues) {
      return
    }

    if (isReader) {
      formik.setValues({
        ...formik.values,
        name: formik.values.name || loggedUserName,
        email: usersEmail,
        date: formik.values.date || usersWeddingDate,
        guests: formik.values.guests || usersNumberOfGuests,
        newsletter: !!user?.will_recieve_emails_updates_from_rmw,
      })
    }
  }, [isReader, user])

  const handleFormTouch = () => {
    if (isReader) {
      return
    }

    if (!hasInteracted.current) {
      hasInteracted.current = true
      signInModal.open({
        modalProps: {
          disabledRedirect: true,
          onSuccess: () => {
            if (isDesktop) {
              return
            }

            modal.open('supplier-enquiry-form', {
              modalProps: {
                enquiryForm: {
                  supplierId: supplierId,
                },
              },
            })
          },
        },
      })
    }
  }

  return (
    <div
      id={SUPPLIER_SECTION_ID.ENQUIRY_NOW}
      className={s.container}
      onChange={handleFormTouch}
    >
      {title && <h2 className={s.title}>{title}</h2>}

      <form onSubmit={formik.handleSubmit} className={s.form}>
        <div className={s.inputs}>
          <InputGroupV3
            errorMessage={
              formik.touched.name && formik.errors.name
                ? formik.errors.name
                : undefined
            }
          >
            <input
              name="name"
              placeholder="Name *"
              disabled={
                fieldsDisabled ||
                (isReader && (!!user?.first_name || !!user?.last_name))
              }
              className={s.input}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </InputGroupV3>
          <InputGroupV3
            errorMessage={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : undefined
            }
          >
            <input
              name="email"
              type="email"
              placeholder="Email *"
              disabled={fieldsDisabled || (isReader && !!user?.email)}
              className={s.input}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </InputGroupV3>

          <InputGroupV3
            errorMessage={
              formik.touched.date && formik.errors.date
                ? formik.errors.date
                : undefined
            }
          >
            <DatepickerButtonV3
              date={formik.values?.date}
              placeholder="When is your Wedding? *"
              disabled={
                fieldsDisabled ||
                (isReader &&
                  formatWeddingDate(
                    user?.event?.wedding_date,
                    user?.event?.wedding_date_type
                  ) !== '')
              }
              onClose={() => {
                if (isDesktop) {
                  modal.close()
                  return
                }
                const currentValues = formik.values
                modal.open('supplier-enquiry-form', {
                  modalProps: {
                    enquiryForm: {
                      initialValues: currentValues,
                      supplierId: supplierId,
                    },
                  },
                })
              }}
              onConfirm={(date, type) => {
                if (isDesktop) {
                  formik.setFieldValue('date', format(date, type))
                  modal.close()
                  return
                }
                const currentValues = formik.values
                modal.open('supplier-enquiry-form', {
                  modalProps: {
                    enquiryForm: {
                      initialValues: {
                        ...currentValues,
                        date: format(date, type),
                      },
                      supplierId: supplierId,
                    },
                  },
                })
              }}
            />
          </InputGroupV3>

          <InputGroupV3
            errorMessage={
              formik.touched.guests && formik.errors.guests
                ? formik.errors.guests
                : undefined
            }
          >
            <input
              name="guests"
              placeholder="Number of guests *"
              disabled={fieldsDisabled}
              inputMode="numeric"
              className={s.input}
              value={formik.values.guests}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </InputGroupV3>

          <InputGroupV3
            errorMessage={
              formik.touched.message && formik.errors.message
                ? formik.errors.message
                : undefined
            }
          >
            <textarea
              name="message"
              rows={4}
              disabled={fieldsDisabled}
              className={s.input}
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </InputGroupV3>

          <div className={s.newsletterAndCaptchaSection}>
            <InputGroupV3>
              <CheckboxV3
                inputProps={{
                  name: 'newsletter',
                  checked: formik.values.newsletter,
                  onChange: formik.handleChange,
                  disabled: fieldsDisabled,
                }}
              >
                <span className={s.newsletterText}>
                  I’d like to receive news and wedding planning advice.{' '}
                  <Link href={ROUTES.PRIVACY}>Privacy Policy</Link>
                </span>
              </CheckboxV3>
            </InputGroupV3>

            <div className={s.recaptchaInfo}>
              This site is protected by reCAPTCHA and the Google{' '}
              <a href="https://policies.google.com/privacy">Privacy Policy</a>{' '}
              and{' '}
              <a href="https://policies.google.com/terms">Terms of Service</a>{' '}
              apply.
            </div>
          </div>
        </div>

        <div className={s.buttons}>
          <ButtonV4
            variant="dark"
            className={s.enquiryBtn}
            type="submit"
            disabled={formDisabled}
          >
            {title}
            <SendMsgIcon />
          </ButtonV4>

          {handleCancelBtn && (
            <ButtonV4
              variant="light"
              className={s.cancelBtn}
              type="button"
              onClick={handleCancelBtn}
            >
              <XIconNew />
              &nbsp;Cancel
            </ButtonV4>
          )}
        </div>
      </form>
    </div>
  )
}
